import { DateTime } from "luxon";

const WELLS_FARGO_DATA_START_ROW_INDEX = 0;

const WELLS_FARGO_COLUMN_INDEXES = {
  Date: 0,
  Amount: 1,
  Description: 4,
};

function normalizeInstitutionName(name) {
  return String(name || "")
    .trim()
    .toLowerCase();
}

function safeCellValue(row, index) {
  return String(row?.[index] ?? "").trim();
}

function isWellsFargoDataRowEmpty(row) {
  return Object.values(WELLS_FARGO_COLUMN_INDEXES).every((idx) => {
    return String(row?.[idx] ?? "").trim() === "";
  });
}

function toIsoDateFromWellsFargo(rawDateValue, rowNumber) {
  const rawDate = String(rawDateValue ?? "").trim();
  // Try to parse as M/d/yyyy first
  let parsed = DateTime.fromFormat(rawDate, "M/d/yyyy");
  if (parsed.isValid) {
    return parsed.toISODate();
  }

  // If not valid, try to parse as Excel serial number (integer or float)
  const excelSerial = Number(rawDate);
  if (!isNaN(excelSerial) && excelSerial > 59) {
    // Excel's leap year bug: 1900-02-29 is day 60
    // Excel serial dates: 1 = 1900-01-01, but JS Date epoch is 1970-01-01
    // Luxon: use DateTime.fromObject({ year, month, day })
    // Excel's day 1 is 1899-12-31 in JS Date (Excel bug: 1900 is not a leap year)
    // So, base date is 1899-12-30 for Excel serials
    const base = DateTime.fromISO("1899-12-30");
    parsed = base.plus({ days: excelSerial });
    if (parsed.isValid) {
      return parsed.toISODate();
    }
  }
  throw new Error(
    `Invalid Wells Fargo date at row ${rowNumber}. Expected M/d/yyyy or Excel serial, found "${rawDate}".`
  );
}

export const wellsFargoAdapter = {
  name: "wells-fargo",
  institutionAliases: ["wells fargo", "wells-fargo", "wells_fargo"],
  supportedDetailFields: [],
  canHandleInstitutionName(institutionName) {
    const normalized = normalizeInstitutionName(institutionName);
    return this.institutionAliases.includes(normalized);
  },
  // Keeping _detailsToInclude parameter for consistency with other adapters, even though Wells Fargo doesn't support additional details in this implementation
  validateDetailsToInclude(_detailsToInclude = []) {
    return {
      normalized: [],
      invalid: [],
      allowed: [],
    };
  },
  // Keeping _options parameter for consistency with other adapters, even though Wells Fargo doesn't support additional options in this implementation
  extractTransactions(rows, _options = {}) {
    if (!Array.isArray(rows) || rows.length === 0) {
      return [];
    }

    const transactions = [];

    for (
      let rowIndex = WELLS_FARGO_DATA_START_ROW_INDEX;
      rowIndex < rows.length;
      rowIndex += 1
    ) {
      const row = rows[rowIndex] || [];

      if (isWellsFargoDataRowEmpty(row)) {
        break;
      }

      const rowNumber = rowIndex + 1;
      const isoDate = toIsoDateFromWellsFargo(
        row[WELLS_FARGO_COLUMN_INDEXES.Date],
        rowNumber
      );

      transactions.push({
        rowNumber,
        date: isoDate,
        amount: safeCellValue(row, WELLS_FARGO_COLUMN_INDEXES.Amount),
        description: safeCellValue(row, WELLS_FARGO_COLUMN_INDEXES.Description),
        details: {},
      });
    }

    return transactions;
  },
};
